import { useState, useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button, Input, Label, Card, CardContent, CardHeader, CardTitle } from "../components/ui"
import { Shell } from "../components/shell"
import { Avatar } from "../components/avatar"
import { useToast } from "../components/toast"
import { Camera, Bell, Shield, ShieldCheck, ShieldOff } from "lucide-react"

type EmailPrefs = {
  emailNotifEnabled: boolean
  emailMentioned: boolean
  emailAssigned: boolean
  emailComment: boolean
  emailPayment: boolean
  emailDueReminder: boolean
}

const DEFAULT_PREFS: EmailPrefs = {
  emailNotifEnabled: true,
  emailMentioned: true,
  emailAssigned: true,
  emailComment: true,
  emailPayment: true,
  emailDueReminder: true,
}

/** Profile page. Lets the user update their name and avatar, manage 2FA, and toggle email notification preferences. */
export default function ProfilePage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: session, refetch } = authClient.useSession()
  const [name, setName] = useState(session?.user.name || "")
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [updatingName, setUpdatingName] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const [twoFactorStep, setTwoFactorStep] = useState<"idle" | "setup" | "verify" | "disable">("idle")
  const [totpUri, setTotpUri] = useState<string | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [totpCode, setTotpCode] = useState("")
  const [twoFaPassword, setTwoFaPassword] = useState("")
  const [twoFaLoading, setTwoFaLoading] = useState(false)

  const { data: emailPrefs } = useQuery({
    queryKey: ["emailPrefs"],
    queryFn: () => fetcher<EmailPrefs>("/api/notifications/prefs"),
  })

  const updatePref = useMutation({
    mutationFn: (body: Partial<EmailPrefs>) =>
      fetcher<EmailPrefs>("/api/notifications/prefs", { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: (data) => {
      queryClient.setQueryData(["emailPrefs"], data)
    },
    onError: () => toast("Failed to update settings", "error"),
  })

  const prefs = emailPrefs || DEFAULT_PREFS

  const togglePref = (key: keyof EmailPrefs, val: boolean) => {
    updatePref.mutate({ [key]: val })
  }

  const startEnable2FA = async () => {
    if (!twoFaPassword) { toast("Enter your password first", "error"); return }
    setTwoFaLoading(true)
    try {
      const res = await authClient.twoFactor.enable({ password: twoFaPassword })
      if (res.data?.totpURI) { setTotpUri(res.data.totpURI); setTwoFactorStep("verify") }
      else toast("Failed to start 2FA setup", "error")
    } catch (e: any) { toast(e.message || "Failed", "error") }
    setTwoFaLoading(false)
  }

  const verifyAndActivate2FA = async () => {
    setTwoFaLoading(true)
    try {
      const res = await (authClient.twoFactor.verifyTotp as any)({ code: totpCode })
      if (res?.data?.backupCodes) setBackupCodes(res.data.backupCodes)
      setTwoFactorStep("idle")
      await refetch()
      toast("Two-factor authentication enabled", "success")
    } catch (e: any) { toast(e.message || "Invalid code", "error") }
    setTwoFaLoading(false)
  }

  const disable2FA = async () => {
    if (!twoFaPassword) { toast("Enter your password first", "error"); return }
    setTwoFaLoading(true)
    try {
      await authClient.twoFactor.disable({ password: twoFaPassword })
      setTwoFactorStep("idle")
      setTwoFaPassword("")
      await refetch()
      toast("Two-factor authentication disabled", "success")
    } catch (e: any) { toast(e.message || "Failed", "error") }
    setTwoFaLoading(false)
  }

  if (!session) {
    navigate({ to: "/login" })
    return null
  }

  const avatarSrc = avatarPreview || session.user.image || null

  const updateName = async (e: React.FormEvent) => {
    e.preventDefault()
    setUpdatingName(true)
    try {
      await authClient.updateUser({ name })
      await refetch()
      toast("Name updated", "success")
    } catch (err: any) {
      toast(err.message || "Failed to update name", "error")
    }
    setUpdatingName(false)
  }

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      toast("New passwords do not match", "error")
      return
    }
    if (newPassword.length < 8) {
      toast("New password must be at least 8 characters", "error")
      return
    }
    setChangingPassword(true)
    try {
      await authClient.changePassword({ currentPassword, newPassword, revokeOtherSessions: false })
      toast("Password changed", "success")
      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
    } catch (err: any) {
      toast(err.message || "Failed to change password", "error")
    }
    setChangingPassword(false)
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      toast("Image must be under 2 MB", "error")
      return
    }
    const reader = new FileReader()
    reader.onload = (ev) => setAvatarPreview(ev.target?.result as string)
    reader.readAsDataURL(file)

    setUploadingAvatar(true)
    try {
      const form = new FormData()
      form.append("file", file)
      const res = await fetcher<{ url: string }>("/api/attachments/avatar", { method: "POST", body: form })
      await authClient.updateUser({ image: res.url })
      await refetch()
      toast("Profile picture updated", "success")
    } catch (err: any) {
      toast(err.message || "Failed to upload picture", "error")
      setAvatarPreview(null)
    }
    setUploadingAvatar(false)
  }

  return (
    <Shell title="Profile" back={{ to: "/", label: "Workspaces" }}>
      <main className="mx-auto w-full max-w-2xl space-y-6 p-6">
        <div className="flex flex-col items-center gap-3 py-4">
          <div className="relative">
            <Avatar id={session.user.id} name={session.user.name} email={session.user.email} image={avatarSrc} size="lg" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingAvatar}
              className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full border border-border bg-card shadow-sm hover:bg-muted"
              title="Change profile picture"
            >
              <Camera className="h-3.5 w-3.5" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
          </div>
          <div className="text-center">
            <h2 className="text-xl font-semibold tracking-tight">{session.user.name || "Your profile"}</h2>
            <p className="text-sm text-muted-foreground">{session.user.email}</p>
          </div>
        </div>

        <Card className="rounded-xl border-border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium">Profile details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={updateName} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input value={session.user.email} disabled />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <Button type="submit" isLoading={updatingName} className="w-full">Update name</Button>
            </form>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium">Change password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={changePassword} className="space-y-4">
              <div>
                <Label>Current password</Label>
                <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
              </div>
              <div>
                <Label>New password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              </div>
              <div>
                <Label>Confirm new password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={confirmPassword && confirmPassword !== newPassword ? "border-red-400 focus-visible:ring-red-400" : ""}
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
                )}
              </div>
              <Button type="submit" isLoading={changingPassword} className="w-full" disabled={!!(confirmPassword && confirmPassword !== newPassword)}>
                Change password
              </Button>
            </form>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-medium">
              <Bell className="h-4 w-4" /> Notification settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Email notifications</p>
                  <p className="text-xs text-muted-foreground">Get notified by email when things happen</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={prefs.emailNotifEnabled}
                  onClick={() => togglePref("emailNotifEnabled", !prefs.emailNotifEnabled)}
                  className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    prefs.emailNotifEnabled ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-md transition-transform ${
                    prefs.emailNotifEnabled ? "translate-x-4" : "translate-x-0"
                  }`} />
                </button>
              </div>
              {prefs.emailNotifEnabled && (
                <div className="space-y-3 border-t border-border pt-3">
                  {([
                    { key: "emailAssigned", label: "Card assigned to me" },
                    { key: "emailMentioned", label: "@mentioned in a comment" },
                    { key: "emailComment", label: "Activity on cards I follow" },
                    { key: "emailDueReminder", label: "Due date reminders" },
                    { key: "emailPayment", label: "Billing & payment events" },
                  ] as { key: keyof EmailPrefs; label: string }[]).map(({ key, label }) => (
                    <label key={key} className="flex cursor-pointer items-center justify-between gap-4">
                      <span className="text-sm">{label}</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={prefs[key]}
                        onClick={() => togglePref(key, !prefs[key])}
                        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                          prefs[key] ? "bg-primary" : "bg-muted"
                        }`}
                      >
                        <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-md transition-transform ${
                          prefs[key] ? "translate-x-4" : "translate-x-0"
                        }`} />
                      </button>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        {/* 2FA Card */}
        <Card className="rounded-xl border-border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-medium">
              {(session.user as any).twoFactorEnabled
                ? <ShieldCheck className="h-4 w-4 text-green-500" />
                : <Shield className="h-4 w-4" />}
              Two-factor authentication
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(session.user as any).twoFactorEnabled ? (
              <>
                <p className="mb-4 text-sm text-muted-foreground">
                  <span className="font-medium text-green-600 dark:text-green-400">Enabled.</span> Your account is protected with an authenticator app.
                </p>
                {twoFactorStep === "disable" ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Enter your password to disable 2FA:</p>
                    <Input type="password" placeholder="Current password" value={twoFaPassword} onChange={(e) => setTwoFaPassword(e.target.value)} />
                    <div className="flex gap-2">
                      <Button variant="destructive" size="sm" onClick={disable2FA} isLoading={twoFaLoading}>Disable 2FA</Button>
                      <Button variant="outline" size="sm" onClick={() => { setTwoFactorStep("idle"); setTwoFaPassword("") }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setTwoFactorStep("disable")}>
                    <ShieldOff className="mr-1.5 h-3.5 w-3.5" /> Disable 2FA
                  </Button>
                )}
              </>
            ) : twoFactorStep === "verify" && totpUri ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code to confirm.</p>
                <div className="flex justify-center rounded-lg border border-border bg-white p-3">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(totpUri)}`} alt="TOTP QR Code" className="h-44 w-44" />
                </div>
                <div className="space-y-1">
                  <Label>Verification code</Label>
                  <Input
                    placeholder="000000"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && verifyAndActivate2FA()}
                    className="text-center text-lg tracking-widest"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={verifyAndActivate2FA} isLoading={twoFaLoading} disabled={totpCode.length !== 6}>Activate 2FA</Button>
                  <Button variant="outline" size="sm" onClick={() => { setTwoFactorStep("idle"); setTotpUri(null); setTwoFaPassword("") }}>Cancel</Button>
                </div>
                {backupCodes.length > 0 && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900/40 dark:bg-yellow-950/20">
                    <p className="mb-2 text-xs font-semibold text-yellow-800 dark:text-yellow-300">Save these backup codes. They won't be shown again:</p>
                    <div className="grid grid-cols-2 gap-1">
                      {backupCodes.map((code) => <code key={code} className="text-xs font-mono">{code}</code>)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Add an extra layer of security. You'll need your password to begin setup.</p>
                <div className="space-y-1">
                  <Label>Password</Label>
                  <Input type="password" placeholder="Current password" value={twoFaPassword} onChange={(e) => setTwoFaPassword(e.target.value)} />
                </div>
                <Button size="sm" onClick={startEnable2FA} isLoading={twoFaLoading} disabled={!twoFaPassword}>
                  <Shield className="mr-1.5 h-3.5 w-3.5" /> Set up 2FA
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </Shell>
  )
}
